import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { generatePasscode } from '@/utils/storage'
import { detectRegion, setRegion as persistRegion, type Region } from '@/utils/time'
import { REGION_DATA } from './HealthDropdown'

const ALL_REGIONS: Region[] = ['AU', 'UK', 'US', 'NZ']

interface PublicNavBarProps {
  onMenuClick: () => void
}

export default function PublicNavBar({ onMenuClick }: PublicNavBarProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const [region, setRegion] = useState<Region>(detectRegion)
  const [flagPickerOpen, setFlagPickerOpen] = useState(false)
  const flagRef = useRef<HTMLDivElement>(null)

  // Close flag picker on click outside
  useEffect(() => {
    if (!flagPickerOpen) return
    const handleMouseDown = (e: MouseEvent) => {
      if (flagRef.current && !flagRef.current.contains(e.target as Node)) {
        setFlagPickerOpen(false)
      }
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [flagPickerOpen])

  const handleGetStarted = () => {
    const newPasscode = generatePasscode()
    navigate(`/u/${newPasscode}`)
  }

  const handleLogIn = () => {
    if (location.pathname === '/') {
      document.getElementById('enter-key')?.scrollIntoView({ behavior: 'smooth' })
    } else {
      navigate('/#enter-key')
    }
  }

  return (
    <nav className="sticky top-0 z-40 bg-[var(--bg-secondary)] border-b border-[var(--border)]" role="navigation" aria-label="Main navigation">
      <div className="flex items-center justify-between h-14 px-4">
        {/* Hamburger Menu */}
        <button
          onClick={onMenuClick}
          className="p-2 rounded-lg hover:bg-[var(--bg-card)] transition-colors cursor-pointer"
          aria-label="Open navigation menu"
        >
          <svg className="w-6 h-6 text-[var(--text-primary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
          <svg className="w-8 h-8 text-[var(--accent-teal)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 4h8l-1 16H9L8 4z" />
            <ellipse cx="12" cy="4" rx="4" ry="1.5" />
            <circle cx="10" cy="10" r="0.5" fill="currentColor" />
            <circle cx="14" cy="8" r="0.5" fill="currentColor" />
            <circle cx="11" cy="14" r="0.5" fill="currentColor" />
          </svg>
          <div className="hidden sm:block leading-tight">
            <span className="text-[var(--text-primary)] font-bold tracking-wide">
              SSDL<span className="text-[var(--accent-teal)]">.app</span>
            </span>
            <p className="text-[10px] text-[var(--text-secondary)] -mt-0.5">Simple, Secure Drink Log</p>
          </div>
        </Link>

        {/* Right side */}
        <div className="flex items-center gap-1">
          {/* Flag region picker */}
          <div ref={flagRef} className="relative">
            <button
              onClick={() => setFlagPickerOpen(prev => !prev)}
              className="p-2 rounded-lg hover:bg-[var(--bg-card)] transition-colors cursor-pointer text-base leading-none"
              aria-label={`Region: ${REGION_DATA[region].label}. Click to change.`}
              aria-haspopup="listbox"
              aria-expanded={flagPickerOpen}
            >
              {REGION_DATA[region].flag}
            </button>
            {flagPickerOpen && (
              <div
                role="listbox"
                aria-label="Select region"
                className="absolute right-0 top-full mt-1 bg-[var(--bg-secondary)] border border-[var(--border)] rounded-lg shadow-lg z-50 overflow-hidden whitespace-nowrap"
              >
                {ALL_REGIONS.map(r => (
                  <button
                    key={r}
                    role="option"
                    aria-selected={r === region}
                    onClick={() => { setRegion(r); persistRegion(r); setFlagPickerOpen(false) }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm cursor-pointer transition-colors ${
                      r === region
                        ? 'bg-[var(--bg-card)] text-[var(--text-primary)]'
                        : 'text-[var(--text-secondary)] hover:bg-[var(--bg-card)] hover:text-[var(--text-primary)]'
                    }`}
                  >
                    <span className="text-base">{REGION_DATA[r].flag}</span>
                    <span>{REGION_DATA[r].label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Get Started */}
          <button
            onClick={handleGetStarted}
            className="btn btn-primary text-sm py-1.5 px-3"
          >
            Get Started
          </button>

          {/* Log In */}
          <button
            onClick={handleLogIn}
            className="text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] px-2 py-1.5 cursor-pointer transition-colors"
          >
            Log In
          </button>
        </div>
      </div>
    </nav>
  )
}
